'use client'

import { useLaunchDarkly } from '@/providers/LaunchDarkly'
import { useTrustedIdentityScopes } from '@/hooks/useTrustedIdentityScopes'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'
import { canAccessClientPortalFiles } from '@/utilities/auth/clientPortalFilesRoleAccess'
import { areClientFeatureFlagContextsEqual } from '@/utilities/client/clientPortalContext'
import { schemas, type FileAccessFlagValueType } from '@/types/portal-api.generated'

export const CLIENT_FILES_FLAG = 'client-portal-files'

export const FileAccessFlagValue = schemas.FileAccessFlagValue.enum

export type ClientFilesFlagValue = FileAccessFlagValueType

export function parseClientFilesFlag(value: unknown): ClientFilesFlagValue | null {
  const result = schemas.FileAccessFlagValue.safeParse(value)
  return result.success ? result.data : null
}

function flagCanGrantFilesAccess(value: ClientFilesFlagValue | null): boolean {
  return value === FileAccessFlagValue.read || value === FileAccessFlagValue.write
}

/**
 * Gives the Files access for the partner. The nav and the Files page must both use this
 * hook so that they show the same decision.
 *
 * `access` stays null while flags for that partner are not ready. A disabled flag, a
 * missing flag, or a failed flag fetch sets access to disabled and does not wait for
 * identity. A read or write flag waits for the partner role.
 *
 * Role checks mirror portal-api client files IdentityRoleGuard and is_client_super_admin.
 * Files are denied when the active partner scope has a non-PHI org or program role.
 *
 * When scopes is null after the role check is ready, this hook denies Files access.
 */
export function useClientFilesAccess(partnerId: string | null | undefined): {
  access: ClientFilesFlagValue | null
  isAccessReady: boolean
  canViewFiles: boolean
  canWriteFiles: boolean
} {
  const { flags, isLoading, hasFetched, error, clientContext } = useLaunchDarkly()
  const normalizedPartnerId = normalizeNullableClientId(partnerId)
  const isPartnerAligned = normalizedPartnerId
    ? areClientFeatureFlagContextsEqual(clientContext, {
        kind: 'partner',
        partnerId: normalizedPartnerId,
      })
    : false
  const areFlagsSettled = isPartnerAligned && !isLoading && (hasFetched || error !== null)
  const parsedValue = parseClientFilesFlag(flags[CLIENT_FILES_FLAG])
  const hasUsableFlags = error === null
  const flagGrantsAccess = areFlagsSettled && hasUsableFlags && flagCanGrantFilesAccess(parsedValue)

  const { scopes, isReady: isRoleCheckReady } = useTrustedIdentityScopes({
    enabled: flagGrantsAccess,
    errorMessage: 'Error checking Client Portal Files access:',
  })
  const hasAllowedRole = canAccessClientPortalFiles(scopes, normalizedPartnerId)

  let access: ClientFilesFlagValue | null = null
  if (areFlagsSettled && !flagGrantsAccess) {
    access = FileAccessFlagValue.disabled
  } else if (flagGrantsAccess && isRoleCheckReady) {
    access = hasAllowedRole ? parsedValue : FileAccessFlagValue.disabled
  }

  return {
    access,
    isAccessReady: access !== null,
    canViewFiles: access === FileAccessFlagValue.read || access === FileAccessFlagValue.write,
    canWriteFiles: access === FileAccessFlagValue.write,
  }
}
